import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { onedriveAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Cloud,
  CloudOff,
  ExternalLink,
  Loader2,
  CheckCircle,
  XCircle,
  RefreshCw,
  HardDrive,
  Unplug,
  Folder,
  FolderCheck,
  Save,
} from "lucide-react";

export function OneDriveSettingsPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authorizing, setAuthorizing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [message, setMessage] = useState(null);
  const [allFolders, setAllFolders] = useState([]);
  const [allowedFolders, setAllowedFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [savingFolders, setSavingFolders] = useState(false);

  useEffect(() => {
    loadStatus();
    // Check for callback result
    if (searchParams.get("success") === "true") {
      setMessage({ text: "OneDrive connesso con successo!", type: "success" });
    } else if (searchParams.get("error")) {
      const detail = searchParams.get("detail");
      let errorText = "Errore nella connessione a OneDrive. Riprova.";
      if (detail) {
        const decoded = decodeURIComponent(detail);
        if (decoded.includes("401")) {
          errorText = "Credenziali non valide (401). Verifica che il Client Secret sia il 'Value' e non il 'Secret ID' su Azure Portal.";
        } else if (decoded.includes("redirect_uri")) {
          errorText = "Redirect URI non corrispondente. Verifica la configurazione su Azure Portal.";
        } else {
          errorText = `Errore: ${decoded}`;
        }
      }
      setMessage({ text: errorText, type: "error" });
    }
  }, []);

  const loadStatus = async () => {
    try {
      const res = await onedriveAPI.getStatus();
      setStatus(res.data);
      if (res.data.connected) {
        loadFolderSettings();
      }
    } catch {
      setStatus({ configured: false, connected: false });
    } finally {
      setLoading(false);
    }
  };

  const loadFolderSettings = async () => {
    setLoadingFolders(true);
    try {
      const [allRes, allowedRes] = await Promise.all([
        onedriveAPI.getAllRootFolders(),
        onedriveAPI.getAllowedFolders(),
      ]);
      setAllFolders(allRes.data.folders);
      setAllowedFolders(allowedRes.data.allowed_folders);
    } catch { /* ignore */ }
    finally { setLoadingFolders(false); }
  };

  const toggleFolder = (name) => {
    setAllowedFolders((prev) =>
      prev.includes(name) ? prev.filter((f) => f !== name) : [...prev, name]
    );
  };

  const saveAllowedFolders = async () => {
    setSavingFolders(true);
    try {
      await onedriveAPI.setAllowedFolders(allowedFolders);
      setMessage({ text: "Cartelle visibili aggiornate", type: "success" });
    } catch {
      setMessage({ text: "Errore nel salvataggio", type: "error" });
    } finally {
      setSavingFolders(false);
    }
  };

  const handleAuthorize = async () => {
    setAuthorizing(true);
    try {
      const res = await onedriveAPI.authorize();
      window.location.href = res.data.authorization_url;
    } catch (err) {
      setMessage({ text: err.response?.data?.detail || "Errore nell'avvio della connessione", type: "error" });
      setAuthorizing(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await onedriveAPI.disconnect();
      setStatus((prev) => ({ ...prev, connected: false }));
      setMessage({ text: "OneDrive disconnesso", type: "success" });
      setTestResult(null);
      setAllFolders([]);
      setAllowedFolders([]);
    } catch {
      setMessage({ text: "Errore nella disconnessione", type: "error" });
    } finally {
      setDisconnecting(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await onedriveAPI.testConnection();
      setTestResult({ success: true, data: res.data });
    } catch (err) {
      setTestResult({ success: false, error: err.response?.data?.detail || "Test fallito" });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-[#2D7BB7]" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
      {message && (
        <div
          className={`px-4 py-3 rounded-lg shadow-sm ${
            message.type === "error"
              ? "bg-red-50 text-red-700 border border-red-200"
              : "bg-emerald-50 text-emerald-700 border border-emerald-200"
          }`}
          data-testid="onedrive-message"
        >
          {message.text}
        </div>
      )}

      <div>
        <h1 className="text-3xl font-heading font-bold text-foreground" data-testid="onedrive-title">
          Microsoft OneDrive
        </h1>
        <p className="text-muted-foreground mt-1">
          Gestione connessione OneDrive per il salvataggio degli allegati
        </p>
      </div>

      {/* Status Card */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
            <HardDrive size={20} className="text-[#2D7BB7]" />
            Stato Connessione
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {status?.connected ? (
                <Cloud size={32} className="text-emerald-500" />
              ) : (
                <CloudOff size={32} className="text-muted-foreground" />
              )}
              <div>
                <p className="font-medium text-foreground">
                  {status?.connected ? "Connesso" : "Non connesso"}
                </p>
                {status?.connected && status?.user_email && (
                  <p className="text-sm text-muted-foreground">
                    {status.user_name} ({status.user_email})
                  </p>
                )}
              </div>
            </div>
            <Badge
              className={
                status?.connected
                  ? "bg-emerald-100 text-emerald-700 border-emerald-300"
                  : "bg-gray-100 text-gray-600 border-gray-300"
              }
              data-testid="onedrive-status-badge"
            >
              {status?.connected ? "Attivo" : "Inattivo"}
            </Badge>
          </div>

          {!status?.configured && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              Le credenziali OneDrive non sono configurate. Contatta l'amministratore.
            </div>
          )}

          {status?.connected && status?.connected_at && (
            <p className="text-sm text-muted-foreground">
              Connesso dal: {new Date(status.connected_at).toLocaleString("it-IT")}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Actions Card */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-foreground">Azioni</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!status?.connected ? (
            <Button
              onClick={handleAuthorize}
              disabled={authorizing || !status?.configured}
              className="w-full bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white"
              data-testid="onedrive-connect-btn"
            >
              {authorizing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ExternalLink className="mr-2" size={18} />
              )}
              Connetti OneDrive
            </Button>
          ) : (
            <div className="flex gap-3">
              <Button
                onClick={handleTest}
                disabled={testing}
                variant="outline"
                className="flex-1 border-[#2D7BB7] text-[#2D7BB7]"
                data-testid="onedrive-test-btn"
              >
                {testing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2" size={16} />
                )}
                Testa Connessione
              </Button>
              <Button
                onClick={handleDisconnect}
                disabled={disconnecting}
                variant="outline"
                className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                data-testid="onedrive-disconnect-btn"
              >
                {disconnecting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Unplug className="mr-2" size={16} />
                )}
                Disconnetti
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Test Result */}
      {testResult && (
        <Card
          className={`border ${
            testResult.success ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"
          }`}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              {testResult.success ? (
                <CheckCircle size={20} className="text-emerald-600" />
              ) : (
                <XCircle size={20} className="text-red-600" />
              )}
              <span
                className={`font-medium ${
                  testResult.success ? "text-emerald-700" : "text-red-700"
                }`}
              >
                {testResult.success ? "Connessione funzionante" : "Test fallito"}
              </span>
            </div>
            {testResult.success && testResult.data?.items && (
              <p className="text-sm text-emerald-700">
                {testResult.data.items_count} elementi trovati nella root di OneDrive
              </p>
            )}
            {testResult.error && (
              <p className="text-sm text-red-700">{testResult.error}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Allowed Folders Configuration */}
      {status?.connected && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
              <FolderCheck size={20} className="text-[#2D7BB7]" />
              Cartelle Visibili
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Seleziona le cartelle da mostrare nel browser durante il salvataggio allegati.
              Se nessuna e' selezionata, tutte le cartelle saranno visibili.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {loadingFolders ? (
              <div className="flex items-center gap-2 py-4">
                <Loader2 className="h-5 w-5 animate-spin text-[#2D7BB7]" />
                <span className="text-sm text-muted-foreground">Caricamento cartelle...</span>
              </div>
            ) : allFolders.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">Nessuna cartella trovata su OneDrive</p>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
                  {allFolders.map((folder) => {
                    const isSelected = allowedFolders.includes(folder.name);
                    return (
                      <button
                        key={folder.id}
                        type="button"
                        onClick={() => toggleFolder(folder.name)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-all ${
                          isSelected
                            ? "border-[#2D7BB7] bg-sky-50 text-[#2D7BB7]"
                            : "border-border bg-background text-muted-foreground hover:border-gray-400"
                        }`}
                        data-testid={`allowed-folder-${folder.name}`}
                      >
                        <Folder
                          size={18}
                          className={isSelected ? "text-amber-500" : "text-gray-400"}
                        />
                        <span className="flex-1 text-sm font-medium truncate">{folder.name}</span>
                        {isSelected && (
                          <CheckCircle size={16} className="text-[#2D7BB7] shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between pt-2">
                  <p className="text-xs text-muted-foreground">
                    {allowedFolders.length === 0
                      ? "Tutte le cartelle sono visibili"
                      : `${allowedFolders.length} cartelle selezionate`}
                  </p>
                  <Button
                    onClick={saveAllowedFolders}
                    disabled={savingFolders}
                    size="sm"
                    className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white"
                    data-testid="save-allowed-folders-btn"
                  >
                    {savingFolders ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-2" size={16} />
                    )}
                    Salva
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <p className="text-sm text-blue-800 leading-relaxed">
            Quando OneDrive e connesso, tutti gli allegati caricati nei protocolli
            saranno salvati automaticamente su OneDrive nella cartella{" "}
            <code className="bg-blue-100 px-1 rounded">Protocolli/</code>.
            I file esistenti in locale continueranno ad essere accessibili.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
